import { uniq, path } from 'ramda'
import { getMasterUnitParam, isSomething } from './'


export const GRAPH_TYPES = {
  multiDevice: {
    name: 'Multi Station',
    description: 'Compare a single type of measurement at different stations'
  },
  multiParam: {
    name: 'Multi Parameter',
    description: 'Compare different sensors at one station'
  }
}

export const getGraphMasterUnitParam = param => {
  const p = getMasterUnitParam(param) || param

  // Voltage parameters
  if (/volts/.test(p)) {
    return 'battvolts'
  }

  // Humidity parameters (%)
  if (/^humidity/.test(p) || p === 'humidityin' || p === 'humiditykestrel' || p === 'pm_in_humidity_aqin' || p === 'relativeairdensity' || p === 'pig') {
    return 'humidity'
  }

  // Wind direction parameters (°)
  if (/^winddir/.test(p) || /^windgustdir/.test(p) || /_avg\d+m$/.test(p) && p.includes('dir')) {
    return 'winddir'
  }

  // PM2.5/PM10 air quality parameters (µg/m³)
  if (/^pm25/.test(p) || /^pm10/.test(p)) {
    return 'pm25'
  }

  // CO2 parameters (ppm)
  if (/^co2/.test(p)) {
    return 'co2'
  }

  // Solar radiation parameters (W/m²)
  if (p === 'solarradiation' || p === 'twlwm2') {
    return 'solarradiation'
  }

  // AQI indices (unitless)
  if (/^aqi_/.test(p)) {
    return 'aqi'
  }

  // Leaf wetness parameters (%)
  if (/^leafwetness\d+$/.test(p)) {
    return 'leafwetness'
  }

  return p
}

export const getGraphMasterParams = (graph) => {
  const params = graph.params
    .map(({ param }) => getGraphMasterUnitParam(param))
  return uniq(params)
}

export const CACHE_DATA = true

export const GRAPH_COLORS = [
  '#15A7E2',
  '#57A709',
  '#E07D5A',
  '#9D3EC3',
  '#EA47DF',
  '#6C74E8',
  '#E5514D',
  '#99972B'
]

// Sanitize dailyrainin data to fix out-of-order issues from cached station data
export const sanitizeDailyRainData = (currentDevice, graphData) => {
  // Get timezone from device
  const tzName = path(['tz', 'name'], currentDevice)
  
  // If no timezone or no data, return original data
  if (!tzName || !graphData || graphData.length === 0) return graphData
  
  // Only apply to KestrelMet devices
  const stationType = path(['lastData', 'stationtype'], currentDevice)
  const isKestrelMet = stationType === 'NK_KestrelMet6000' || stationType === 'NK_KestrelMet6000W'
  if (!isKestrelMet) return graphData
  
  // First, sort the data by date (ascending - oldest to newest)
  const sortedData = [...graphData].sort((a, b) => a.dateutc - b.dateutc)
  
  // Process the sorted data to fix decreasing dailyrainin values within the same day
  const sanitized = sortedData.map((point, index) => {
    // Skip first point or points without dailyrainin value
    if (index === 0 || !isSomething(point.dailyrainin)) return point
    
    const prevPoint = sortedData[index - 1]
    
    // If previous point has no dailyrainin, return current point as-is
    if (!isSomething(prevPoint.dailyrainin)) {
      return point
    }
    
    const currentDate = moment.tz(point.dateutc, tzName).startOf('day')
    const prevDate = moment.tz(prevPoint.dateutc, tzName).startOf('day')
    
    // Check if same day
    if (!currentDate.isSame(prevDate)) {
      return point // Different days, no sanitization needed
    }
    
    // Same day: current dailyrainin should be >= previous dailyrainin
    // If it decreased, null it out
    if (point.dailyrainin < prevPoint.dailyrainin) {
      return { ...point, dailyrainin: null }
    }
    
    return point
  })
  
  // Create a map of sanitized values by timestamp
  const sanitizedMap = sanitized.reduce((acc, point) => {
    acc[point.dateutc] = point.dailyrainin
    return acc
  }, {})
  
  // Return data in original order but with sanitized dailyrainin values
  return graphData.map(point => ({
    ...point,
    dailyrainin: sanitizedMap[point.dateutc]
  }))
}